/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react'
import { get } from 'lodash'
import { useParams, useHistory } from 'react-router-dom'
import {
  Button,
  ConfirmationPopup,
  ContentLoader,
  DescriptionTable,
  NotFound,
  Page,
  Tab,
  Tabs,
  TabContent,
  TabsHeader,
  Notification
} from 'shared/components'
import { sendErrorReport, displayValue } from 'shared/helpers'
import { getUserGroup, deleteUserGroup } from 'src/LicenseUsers/actions'
import UserGroupForm from 'src/LicenseUsers/UserGroups/UserGroupForm'
import LicensesTab from './LicensesTab'
import UsersTab from './UsersTab'
import ManagersTab from './ManagersTab'
import './styles.scss'

const UserGroup = () => {
  const history = useHistory()
  const { groupID, companyId } = useParams()

  const [isLoading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [userGroup, setUserGroup] = useState(null)

  const [isEditUserDisplayed, setEditUserDisplayed] = useState(false)

  const [showDeleteUserConfirmation, setDeleteUserConfirmationDisplay] = useState(false)
  const [deletingUserGroup, setDeletingUserGroup] = useState(false)

  const fetchUserGroup = useCallback(() => {
    getUserGroup(companyId, groupID)
      .then(res => {
        const data = get(res, 'data')
        setUserGroup(data)
        setLoading(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot get user group details')
        setLoading(false)
        setNotFound(true)
      })
  }, [companyId, groupID])

  useEffect(() => {
    fetchUserGroup()
  }, [fetchUserGroup])

  const handleDeleteUserGroup = () => {
    setDeletingUserGroup(true)

    deleteUserGroup(companyId, groupID)
      .then(() => {
        Notification('success', __('Changes saved successfully'), __('User group deleted'))
        history.push(`/${companyId}/license_users/user_groups`)
        setDeletingUserGroup(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot delete user group')
        setDeletingUserGroup(false)
        Notification(
          'error',
          __('Your changes were not saved'),
          __('There was an error while saving your changes')
        )
      })
  }

  if (isLoading) {
    return (
      <Page>
        <ContentLoader text={__('Getting license user details')} />
      </Page>
    )
  }
  if (notFound) {
    return (
      <Page>
        <NotFound />
      </Page>
    )
  }

  const CustomerAccountLink = () => {
    if (
      !get(userGroup, 'customer_account') ||
      !get(userGroup, 'customer_account.id') ||
      !get(userGroup, 'customer_account.name')
    )
      return <span>{displayValue(get(userGroup, 'customer_account.name'))}</span>
    const handleClick = () => {
      history.push(`/${companyId}/customer_accounts/${get(userGroup, 'customer_account.id')}`)
    }
    return (
      <span
        className='customer-account-link'
        style={{ cursor: 'pointer', textDecoration: 'underline' }}
        onClick={handleClick}
      >
        {get(userGroup, 'customer_account.name')}
      </span>
    )
  }

  return (
    <Page title={get(userGroup, 'name')}>
      <div className='list-header'>
        <div>
          <Button theme='info' onClick={() => setEditUserDisplayed(true)}>
            {__('Edit user group')}
          </Button>
        </div>
        <div>
          <Button theme='error' onClick={() => setDeleteUserConfirmationDisplay(true)}>
            {__('Delete')}
          </Button>
        </div>
      </div>
      <div className='UserGroup'>
        <DescriptionTable
          details={[
            { label: __('Name'), value: displayValue(get(userGroup, 'name')) },
            { label: __('Code'), value: displayValue(get(userGroup, 'code')) },
            { label: __('Description'), value: displayValue(get(userGroup, 'description')) },
            {
              label: __('Customer account'),
              value: (
                <CustomerAccountLink
                  account={get(userGroup, 'customer_account')}
                  companyId={companyId}
                  history={history}
                />
              )
            },
            {
              label: __('Metadata'),
              value: displayValue(JSON.stringify(get(userGroup, 'metadata')))
            }
          ]}
        />
        <Tabs>
          <TabsHeader>
            <Tab>{__('Users')}</Tab>
            <Tab>{__('Licenses')}</Tab>
            <Tab>{__('License managers')}</Tab>
          </TabsHeader>
          <TabContent>
            <div className='users-tab'>
              <UsersTab />
            </div>
          </TabContent>
          <TabContent>
            <div className='licenses-tab'>
              <LicensesTab />
            </div>
          </TabContent>
          <TabContent>
            <div className='managers-tab'>
              <ManagersTab />
            </div>
          </TabContent>
        </Tabs>
      </div>
      {isEditUserDisplayed && (
        <UserGroupForm
          companyID={companyId}
          closeCb={() => setEditUserDisplayed(false)}
          confirmCb={() => {
            fetchUserGroup()
            setEditUserDisplayed(false)
          }}
          userGroup={userGroup}
        />
      )}
      {showDeleteUserConfirmation && (
        <ConfirmationPopup
          closeCb={() => setDeleteUserConfirmationDisplay(false)}
          confirmCb={handleDeleteUserGroup}
          title={__('Are you sure you want to delete this user group?')}
          confirmText={__('Delete')}
          theme='error'
          disabled={deletingUserGroup}
        >
          {__('User group will be removed from any orders it is assigned to.')}
        </ConfirmationPopup>
      )}
    </Page>
  )
}

export default UserGroup
